import React, { useState, useEffect } from 'react';
import leadApi from '../api/leadApi';
import { MODAL_HIDE_DURATION_MS, MODAL_LAST_SHOWN_KEY } from '../constants';

const ContactModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', // Added phone field
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitMessageType, setSubmitMessageType] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [hasScrolledHalf, setHasScrolledHalf] = useState(false);

  useEffect(() => {
    const lastShown = localStorage.getItem(MODAL_LAST_SHOWN_KEY);
    const canShowModal = !lastShown || (Date.now() - parseInt(lastShown, 10) > MODAL_HIDE_DURATION_MS);

    if (canShowModal) {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (scrollY > (documentHeight - viewportHeight) / 2) {
          setHasScrolledHalf(true);
          window.removeEventListener('scroll', handleScroll);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const lastShown = localStorage.getItem(MODAL_LAST_SHOWN_KEY);
    const canShowModal = !lastShown || (Date.now() - parseInt(lastShown, 10) > MODAL_HIDE_DURATION_MS);

    if (hasScrolledHalf && canShowModal) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasScrolledHalf]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name]) {
      setValidationErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitMessageType('');
    setValidationErrors({}); // Clear validation errors on new submission

    try {
      await leadApi.submitLead(formData);
      setSubmitMessage('Thank you for your message! We will get back to you soon.');
      setSubmitMessageType('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); // Clear phone field too
      localStorage.setItem(MODAL_LAST_SHOWN_KEY, Date.now().toString());
      setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Lead submission failed:', error);
      if (error.response && (error.response.status === 400 || error.response.status === 422)) {
        setValidationErrors(error.response.data.errors || {}); // Backend sends errors in 'errors' key
        setSubmitMessage(error.response.data.message || 'Validation failed. Please check your input.');
        setSubmitMessageType('error');
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
        setSubmitMessage(errorMessage);
        setSubmitMessageType('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex justify-center items-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
    >
      <div className="bg-bg-light p-8 rounded-lg shadow-lg max-w-md w-full relative border border-border-subtle">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 text-text-gray hover:text-text-white text-2xl"
        >
          &times;
        </button>
        <h2 className="text-2xl font-bold text-text-white mb-4">Contact Me</h2>
        <p className="text-text-gray mb-4">Have a question or a project idea? Send me a message!</p>

        {submitMessage && (
          <div className={`p-3 mb-4 rounded ${submitMessageType === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
            {submitMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="modal-name" className="block text-text-white text-sm font-bold mb-1">
              Name
            </label>
            <input
              type="text"
              id="modal-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.name ? 'border-red-500' : 'border-border-subtle'}`}
            />
            {validationErrors?.name && <p className="text-red-500 text-xs italic mt-1">{validationErrors.name[0]}</p>}
          </div>
          <div>
            <label htmlFor="modal-email" className="block text-text-white text-sm font-bold mb-1">
              Email
            </label>
            <input
              type="email"
              id="modal-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.email ? 'border-red-500' : 'border-border-subtle'}`}
            />
            {validationErrors?.email && <p className="text-red-500 text-xs italic mt-1">{validationErrors.email[0]}</p>}
          </div>
          <div>
            <label htmlFor="modal-phone" className="block text-text-white text-sm font-bold mb-1">
              Phone
            </label>
            <input
              type="tel" // Use type="tel" for phone numbers
              id="modal-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.phone ? 'border-red-500' : 'border-border-subtle'}`}
            />
            {validationErrors?.phone && <p className="text-red-500 text-xs italic mt-1">{validationErrors.phone[0]}</p>}
          </div>
          <div>
            <label htmlFor="modal-subject" className="block text-text-white text-sm font-bold mb-1">
              Subject
            </label>
            <input
              type="text"
              id="modal-subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.subject ? 'border-red-500' : 'border-border-subtle'}`}
            />
            {validationErrors?.subject && <p className="text-red-500 text-xs italic mt-1">{validationErrors.subject[0]}</p>}
          </div>
          <div>
            <label htmlFor="modal-message" className="block text-text-white text-sm font-bold mb-1">
              Message
            </label>
            <textarea
              id="modal-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.message ? 'border-red-500' : 'border-border-subtle'}`}
            ></textarea>
            {validationErrors?.message && <p className="text-red-500 text-xs italic mt-1">{validationErrors.message[0]}</p>}
          </div>
          <button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-text-white font-bold py-2 px-4 rounded-full focus:outline-none focus:shadow-outline transition-colors duration-300 w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;